import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout"
import { useSnackbar } from "../../../context/popup";
import { useLoading } from "../../../components/Loading";
import { CardContent, CardHeader, FormControl, FormControlLabel, FormHelperText, Grid2, IconButton, Stack, Switch } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import MaterialTypography from "../../../components/MaterialTypography";
import MaterialSelect from "../../../components/MasterSelect";
import { useWorkflowBindingFormHandling } from "./components/WorkflowBindingStateHandle.js";
import MaterialInput from "../../../components/MaterialInput/index.js";
import MaterialBox from "../../../components/MaterialBox/index.js";
import MaterialButton from "../../../components/MaterialButton/index.js";
import { useEffect, useState } from "react";
import { fetchData, fetchDataById, postData, putData } from "../../../services/index.js";
import DropDownConfig from "../../Utils/DropDownConfig.js";

const WorkflowBindingForm = () => {

    const [searchParams] = useSearchParams();
    const workflowBindingId = searchParams.get("id");
    const pageType = searchParams.get("pageType");
    const pageMode = pageType === "view" ? "view" : pageType === "edit" ? "edit" : "add";
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const { setCircleLoading } = useLoading();

    const [workflowStepOptions, setWorkflowStepOptions] = useState([]);
    const [bindingTypeOptions, setBindingTypeOptions] = useState([]); 
    const [bindingOptions, setBindingOptions] = useState([]);    
    const [environmentOptions, setEnvironmentOptions] = useState([]);
    
    const { formData, setFormData, handleChange, checkAllFields } = useWorkflowBindingFormHandling(); 

    //get by id
    useEffect(() => {
        if (pageMode === "add" || !workflowBindingId) {
            return;
        }
        //Fetch data by Id
        const fetchWorkflowBindingById = async (workflowBindingId) => {
            try {
            setCircleLoading(true);
            const response = await fetchDataById(`/orchestration/workflow-binding/${workflowBindingId}`);

            const responseData = response?.data;
            
            if (response.success) {
                const mapped = {
                    // dropdowns 
                    workflowStepId: responseData?.workflowStep?.id
                    ? { value: responseData.workflowStep.id, label: responseData.workflowStep.stepName }
                    : null,

                    bindingTypeId: responseData?.bindingType?.id
                    ? { value: responseData?.bindingType?.id, label: responseData?.bindingType?.keyName }
                    : null,

                    bindingId: responseData?.bindingData?.id
                    ? { value: responseData.bindingData?.id, label: responseData.bindingData?.name }
                    : null,
                    environmentId: responseData?.environment?.id ? 
                    { value: responseData?.environment?.id, label: responseData?.environment?.keyName } : null,
                    bindingConfig: responseData?.bindingConfig ? JSON.stringify(responseData?.bindingConfig) : "",

                    isActive: responseData?.isActive,

                    // status fields
                    workflowStepIdStatus: responseData?.workflowStep?.id ? "success" : "unchecked",
                    bindingTypeIdStatus: responseData?.bindingType?.id ? "success" : "unchecked",
                    bindingIdStatus: responseData?.bindingId ? "success" : "unchecked",
                    environmentIdStatus: responseData?.environment?.id ? "success" : "unchecked",
                    bindingConfigStatus: responseData?.bindingConfig ? "success" : "unchecked",
                };

                setFormData(mapped);  
            }
            } catch (error) {
            console.error("Error fetching data by ID:", error);
            showSnackbar(error?.response?.data?.message || "Network Error","error");
            }finally{
                setCircleLoading(false);
            }
        };
        fetchWorkflowBindingById(workflowBindingId);
    }, [pageMode]);

    useEffect(() => {
        if(pageMode === "view"){
            return
        }
        fetchAllDropdowns();
    }, [pageMode]);

    useEffect(() => {
        if(!formData?.bindingTypeId?.value || pageMode === "view"){
            return
        }
        const fetchBindingOptions = async () => {
            try{
                setCircleLoading(true);
                const response = await fetchData(`/orchestration/binding-types/${formData.bindingTypeId?.value}/bindings?dropdown=true`)
                
                if(response?.success){
                    const formattedItems = response?.data?.items.map((item) => ({
                        value: item.id,
                        label: item?.name
                    }))
                    setBindingOptions(formattedItems);
                }
            }catch(error){
                console.error("Error fetching Data",error);
                showSnackbar(error?.response?.data?.message || "Network Error", "error");
            }finally{
                setCircleLoading(false);
            }
        };
        fetchBindingOptions();
    },[formData?.bindingTypeId])

    //all dropdown api function
    const fetchAllDropdowns = async () => {
        setCircleLoading(true);

        try {
            const [
            workflowResponse,
            bindingTypeResponse,
            environmentResponse
            ] = await Promise.allSettled([
            fetchData(`/reference/orchestration/work-flow-steps?dropdown=true&isActive=true`),
            fetchData(`/orchestration/key-value-store?code=${DropDownConfig.operationBindingType}&dropdown=true&isActive=true`),
            fetchData(`/api-engine/key-value-store?code=${DropDownConfig.apiEnvironment}&dropdown=true&isActive=true`)
            ]);
            
            const formattedWorkflowSteps = workflowResponse?.value?.data?.items?.map((item) => ({
                value: item?.id,
                label: item?.stepName,
            })) || [];

            const formattedBindingTypes = bindingTypeResponse?.value?.data?.items?.map((item) => ({
                value: item?.id,
                label: item?.keyName
            })) || [];

            const formattedEnvOptions = environmentResponse?.value?.data?.items?.map((item) => ({
                value: item?.id,
                label: item?.keyName,
            })) || [];
            
            setWorkflowStepOptions(formattedWorkflowSteps);
            setBindingTypeOptions(formattedBindingTypes);
            setEnvironmentOptions(formattedEnvOptions);

        } catch (error) {
            console.error("Error fetching dropdown data:", error);
            showSnackbar("Error Fetching Dropdown","warning");
        } finally {
            setCircleLoading(false);
        }
    };

    const handleSubmitWorkflowBinding = async () => {
        const payload = {
            workflowStepId: formData?.workflowStepId?.value,
            bindingTypeId: formData?.bindingTypeId?.value,
            bindingId: formData?.bindingId?.value,
            bindingConfig: formData.bindingConfig ? JSON.parse(formData?.bindingConfig) : {},
            environmentId: formData?.environmentId?.value,
            isActive: formData?.isActive
        };
        //Update
        if(workflowBindingId){
            try {
                const response = await putData(`/orchestration/workflow-binding/${workflowBindingId}`, payload);
                
                const responseMessage = response?.message;
                if(response?.success){
                //success message
                    showSnackbar(responseMessage, "success");
                    navigate('/operation/workflow-binding');          
                }
                else{
                    //error message
                    showSnackbar(responseMessage, "error")
                }
            } catch (error) {
                console.error('Error updating data:', error);
                showSnackbar(error?.response?.data?.message || "Network Error","error");
            }
        }
        else{      
        //Submit
            try {
                const response = await postData('/orchestration/workflow-binding', payload);                
                const responseMessage = response?.message;
                if(response?.success){
                    //success message
                    showSnackbar(responseMessage, "success");
                    navigate('/operation/workflow-binding');          
                }
                else{
                    //error message
                    showSnackbar(responseMessage, "error");
                }
            } catch (error) {
                //catch error message
                console.error('Error posting data:', error);
                showSnackbar(error?.response?.data?.message || "Network Error","error")
            }
        }
    };
    
  return (
    <DashboardLayout>
        <CardHeader
        title={
            <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton onClick={() => navigate("/operation/workflow-binding")}>
                        <ArrowBack />
                    </IconButton>
                    <Stack direction="column" spacing={0}>
                    <MaterialTypography variant="h5" fontWeight="medium">
                        Workflow Binding Details
                    </MaterialTypography>
                    <MaterialTypography variant="caption" color="text">
                        Enter Workflow Binding Details along with Workflow Step, Environment and Binding Details.
                    </MaterialTypography>
                    </Stack>
                </Stack>
            </Stack>
        }
        />
        <CardContent>
            <Grid2 container spacing={1}>
                <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                    <MaterialTypography component="label" variant="caption" fontWeight="bold">
                        Workflow Step*
                    </MaterialTypography>
                    <MaterialSelect
                    name="workflowStepId"
                    value={formData.workflowStepId}
                    onChange={handleChange}
                    options={workflowStepOptions}
                    success={formData.workflowStepIdStatus === "success"}
                    error={formData.workflowStepIdStatus === "error"}
                    isDisabled={pageMode === "view"}
                    />
                </Grid2> 
                <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                    <MaterialTypography component="label" variant="caption" fontWeight="bold">
                        Binding Type*
                    </MaterialTypography>
                    <MaterialSelect
                    name="bindingTypeId"
                    value={formData.bindingTypeId}
                    onChange={handleChange}
                    options={bindingTypeOptions}
                    success={formData.bindingTypeIdStatus === "success"}
                    error={formData.bindingTypeIdStatus === "error"}
                    isDisabled={pageMode === "view"}
                    />
                </Grid2>
                <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                    <MaterialTypography component="label" variant="caption" fontWeight="bold">
                        Binding*
                    </MaterialTypography>
                    <MaterialSelect
                    name="bindingId"
                    value={formData.bindingId}
                    onChange={handleChange}
                    options={bindingOptions}
                    success={formData.bindingIdStatus === "success"}
                    error={formData.bindingIdStatus === "error"}
                    isDisabled={pageMode === "view"}
                    />
                </Grid2>
                <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                    <MaterialTypography component="label" variant="caption" fontWeight="bold">
                        Environment*
                    </MaterialTypography>
                    <MaterialSelect
                    name="environmentId"
                    value={formData.environmentId}
                    onChange={handleChange}
                    options={environmentOptions}
                    success={formData.environmentIdStatus === "success"}
                    error={formData.environmentIdStatus === "error"}
                    isDisabled={pageMode === "view" || formData?.bindingId === ""}
                    />
                </Grid2>
                <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                  <MaterialTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                  >
                    Binding Configuration*
                  </MaterialTypography>
                <MaterialInput
                  name="bindingConfig"
                  placeholder={`eg. {    "url": "https://example.com/api",
            "retries": 3
        }`}
                  value={formData.bindingConfig}
                  onChange={handleChange}
                  error={formData.bindingConfigStatus === "error" ? true : false}
                  success={formData.bindingConfigStatus === "success" ? true : false}
                  multiline
                  rows={3}
                />
                {formData.bindingConfigStatus === "error" && (
                  <FormControl error>
                    <FormHelperText>
                      Binding Configuration should be maximum 20 characters long and can contain
                      only alphabets
                    </FormHelperText>
                  </FormControl>
                )}
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 4 }}>
                    <MaterialTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                    >
                        Active
                    </MaterialTypography>
                    <FormControlLabel
                    control={
                        <Switch
                        checked={formData.isActive}
                        onChange={(e) =>
                            handleChange({
                            target: {
                                name: "isActive",
                                value: e.target.checked,
                            },
                            })
                        }
                        disabled={pageMode === "view"}
                        color="primary"
                        />
                    }
                    label={
                        <MaterialTypography variant="caption" fontWeight="bold">
                        {formData.isActive ? "Yes" : "No"}
                        </MaterialTypography>
                    }
                    sx={{ marginLeft: 0 }}
                    />
                </Grid2>           
            </Grid2>
        </CardContent>
        <MaterialBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        >
            {pageMode !== "view" && (
            <>
                <MaterialButton
                sx={{ m: 2 }}
                variant="outlined"
                color="dark"
                onClick={() => navigate("/operation/workflow-binding")}
                >
                Cancel
                </MaterialButton>
                <MaterialButton
                sx={{ m: 2 }}
                variant="gradient"
                color="dark"
                disabled={!checkAllFields()}
                onClick={handleSubmitWorkflowBinding}
                >
                {pageMode === "edit" ? "Update" : "Submit"}
                </MaterialButton>
            </>
            )}
        </MaterialBox>
    </DashboardLayout>
  )
}

export default WorkflowBindingForm